import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchHelpSupportHelp,
  updateHelpSupportHelp,
  setPage,
  setPageSize,
  setFilters
} from '../../../slices/helpSupportHelpSlice';
import Pagination from '../../../components/Pagination';
import EditableTable from '../../../components/EditableTable';
import Modal from '../../../components/Modal';
import ChangeLog from '../../../components/ChangeLog';
import Loader from "../../../components/Loader";
import Swal from "sweetalert2";
import PageSizeSelector from '../../../components/PageSizeSelector';



const Help = () => {
  const dispatch = useDispatch();
  const {
    helpSupportHelpList,
    currentPage,
    pageSize,
    totalPages,
    inProgressFilter,
    isResolveFilter,
    search,
    status,
    error
  } = useSelector((state) => state.helpSupportHelp);

  const [editRowId, setEditRowId] = useState(null); // Track which row is in edit mode
  const [editedData, setEditedData] = useState({}); // Store temporary data for edited row

  const currentItems = Array.isArray(helpSupportHelpList.result) ? helpSupportHelpList.result : [];
  const editableFields = ['responseMsg', 'queryResolve'];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentLog, setCurrentLog] = useState([]);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Local state for filter inputs
  const [searchVal, setSearchVal] = useState(search || "");

  // Sync local input values with redux store state (e.g. when cleared)
  useEffect(() => {
    setSearchVal(search);
  }, [search]);

  // Main useEffect to fetch paginated/filtered data
  useEffect(() => {
    dispatch(
      fetchHelpSupportHelp({
        page: currentPage,
        page_size: pageSize,
        in_progress: inProgressFilter,
        is_resolve: isResolveFilter,
        search: search
      })
    );
  }, [dispatch, currentPage, pageSize, inProgressFilter, isResolveFilter, search]);

  const handleView = (row) => {
    setViewData(row);
    setIsViewModalOpen(true);
  };

  const handleProcessQuery = async (id) => {
    await dispatch(updateHelpSupportHelp({ id, queryInProgress: true }));
    dispatch(fetchHelpSupportHelp({
      page: currentPage,
      page_size: pageSize,
      in_progress: inProgressFilter,
      is_resolve: isResolveFilter,
      search: search
    }));
    setViewData((prev) => (prev ? { ...prev, queryInProgress: true } : null));
  };

  const handleResolveQuery = async (id, responseMsg) => {
    await dispatch(updateHelpSupportHelp({ id, queryResolve: true, responseMsg }));
    dispatch(fetchHelpSupportHelp({
      page: currentPage,
      page_size: pageSize,
      in_progress: inProgressFilter,
      is_resolve: isResolveFilter,
      search: search
    }));
    setViewData((prev) => (prev ? { ...prev, queryResolve: true, responseMsg } : null));
  };

  const handlePageChange = (page) => {
    dispatch(setPage(page));
  };

  const handlePageSizeChange = (size) => {
    dispatch(setPageSize(size));
  };

  const handleSearchSubmit = () => {
    dispatch(setFilters({ search: searchVal }));
  };

  const handleStatusChange = (val) => {
    if (val === "All") {
      dispatch(setFilters({ inProgressFilter: "", isResolveFilter: "" }));
    } else if (val === "Pending") {
      dispatch(setFilters({ inProgressFilter: "false", isResolveFilter: "false" }));
    } else if (val === "In progress") {
      dispatch(setFilters({ inProgressFilter: "true", isResolveFilter: "false" }));
    } else if (val === "Resolved") {
      dispatch(setFilters({ inProgressFilter: "", isResolveFilter: "true" }));
    }
  };

  const handleClearFilters = () => {
    setSearchVal("");
    dispatch(setFilters({ inProgressFilter: "", isResolveFilter: "", search: "" }));
  };

  const statusVal =
    inProgressFilter === "" && isResolveFilter === ""
      ? "All"
      : isResolveFilter === "true"
      ? "Resolved"
      : inProgressFilter === "true"
      ? "In progress"
      : "Pending";

  const handleEdit = (rowId) => {
    setEditRowId(rowId);
    const rowData = helpSupportHelpList.result?.find((user) => user.id === rowId);
    setEditedData(rowData); // Pre-fill the fields with current row data
  };

  const handleSave = async () => {
    // Extract only id, point, and detail for the API call
    const { id, queryResolve, responseMsg } = editedData;
    const updatedData = { id, queryResolve, responseMsg };
    await dispatch(updateHelpSupportHelp(updatedData));
    dispatch(fetchHelpSupportHelp({
      page: currentPage,
      page_size: pageSize,
      in_progress: inProgressFilter,
      is_resolve: isResolveFilter,
      search: search
    }));
    setEditRowId(null); // Exit edit mode after saving
  };

  const handleCancel = () => {
    setEditRowId(null); // Exit edit mode and reset data
    setEditedData({}); // Reset to original data
  };

  const handleLog = (id) => {
    const logData = helpSupportHelpList?.Change_Log?.filter((f) => f.id === id)[0]?.oldTrack;
    setCurrentLog(logData)
    openModal()
  }

  const handleChange = (e, field, val) => {
    if (field === "active" || field === "status" || field === 'queryResolve') {
      setEditedData((prev) => ({
        ...prev,
        [field]: !val,
      }));
    } else {
      setEditedData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    }
  };



  const headers = helpSupportHelpList.result?.length > 0 ? Object.keys(helpSupportHelpList.result[0]).filter((key) => key !== 'id') : [];

  if (status === 'loading') {
    return <Loader />
  }

  const hasData = helpSupportHelpList.result && helpSupportHelpList.result.length > 0;

  return (
    <div className="w-full flex items-center flex-col pt-4">

      {/* Search & Filter Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 w-11/12 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by ID..."
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
            className="block w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-50/50 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Status:</span>
            <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200/40 shadow-inner">
              {["All", "Pending", "In progress", "Resolved"].map((statusOption) => {
                const isActive = statusVal === statusOption;
                return (
                  <button
                    key={statusOption}
                    type="button"
                    onClick={() => handleStatusChange(statusOption)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all duration-200 active:scale-95 ${
                      isActive
                        ? "bg-white text-[var(--mainCol)] shadow-sm"
                        : "text-gray-500 hover:text-gray-900 hover:bg-white/50"
                    }`}
                  >
                    {statusOption}
                  </button>
                );
              })}
            </div>
          </div>

          {(searchVal || statusVal !== "All") && (
            <button
              onClick={handleClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-semibold"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="max-w-[91%] w-full overflow-scroll flex justify-center">
        {hasData ? (
          <EditableTable
            headers={headers}
            rows={currentItems}
            editRowId={editRowId}
            editedData={editedData}
            handleEdit={handleEdit}
            handleSave={handleSave}
            handleCancel={handleCancel}
            handleChange={handleChange}
            handleLog={handleLog}
            editableFields={editableFields} // Pass editable field
            handleView={handleView}
          // hideChangeLog={true}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <div className="text-5xl mb-3">🔍</div>
            <div className="text-lg font-medium">No Data Available</div>
            <div className="text-sm text-gray-400 mt-1">
              There are no records matching the criteria.
            </div>
          </div>
        )}
      </div>

      {hasData && (
        <div className="flex mt-4 items-center justify-between w-11/12">
          <PageSizeSelector
            pageSize={pageSize}
            setPageSize={handlePageSizeChange}
          />

          <Pagination
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            totalPages={totalPages}
          />
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="Change Log"
        content={<ChangeLog data={currentLog} />}
      />

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Help Details"
        content={<HelpDetailsView data={viewData} onProcess={handleProcessQuery} onResolve={handleResolveQuery} />}
      />
    </div>
  );
};

const HelpDetailsView = ({ data, onProcess, onResolve }) => {
  if (!data) return null;

  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleResolveClick = () => {
    Swal.fire({
      title: "Resolve Query",
      html: `
        <div style="text-align: left; font-size: 13px;">
          <div style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center;">
            <span style="font-weight: bold; color: #4b5563; width: 85px; font-size: 11px; text-transform: uppercase; flex-shrink: 0;">Query Type:</span>
            <span style="padding: 4px 8px; background-color: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb; font-weight: 500; color: #111827; font-size: 12px; flex-grow: 1;">${String(data.queryType || data.query_type || data.QueryType || data.query || "N/A").trim()}</span>
          </div>
          <div style="display: flex; gap: 8px; margin-bottom: 12px; align-items: flex-start;">
            <span style="font-weight: bold; color: #4b5563; width: 85px; font-size: 11px; text-transform: uppercase; flex-shrink: 0; margin-top: 4px;">Message:</span>
            <span style="padding: 4px 8px; background-color: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb; color: #111827; font-size: 12px; flex-grow: 1; white-space: pre-wrap; word-break: break-all; max-height: 80px; overflow-y: auto;">${String(data.msg || data.message || data.body || "N/A").trim()}</span>
          </div>
          <div>
            <strong style="color: #4b5563; font-size: 11px; text-transform: uppercase;">Response Message:</strong>
            <textarea id="swal-response-msg" class="swal2-textarea" style="margin: 4px 0 0 0; width: 100%; height: 70px; min-height: 60px; box-sizing: border-box; font-size: 13px; padding: 8px;" placeholder="Type response here..."></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Resolve",
      confirmButtonColor: "#16a34a",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const responseMsg = document.getElementById("swal-response-msg").value;
        if (!responseMsg || !responseMsg.trim()) {
          Swal.showValidationMessage("You need to write a response message to resolve the query!");
          return false;
        }
        return responseMsg.trim();
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        onResolve(data.id, result.value);
      }
    });
  };

  return (
    <div className="space-y-4 text-sm text-gray-700 max-w-4xl w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Object.entries(data)
          .sort(([keyA], [keyB]) => {
            const isEndA = keyA === "msg" || keyA === "responseMsg" || keyA === "response_msg";
            const isEndB = keyB === "msg" || keyB === "responseMsg" || keyB === "response_msg";
            if (isEndA && !isEndB) return 1;
            if (!isEndA && isEndB) return -1;
            if (isEndA && isEndB) {
              if (keyA === "msg") return -1;
              if (keyB === "msg") return 1;
            }
            return 0;
          })
          .map(([key, val]) => {
          if (key === "id") return null;

          let displayVal = "";
          if (key === "UserId" || key === "userId" || key === "user_id") {
            displayVal = (
              <Link
                to={`/panel-user/${val}`}
                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
              >
                {String(val ?? "")}
              </Link>
            );
          } else if (val === true) {
            displayVal = (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Yes
              </span>
            );
          } else if (val === false) {
            displayVal = (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                No
              </span>
            );
          } else {
            displayVal = <span className="font-medium break-all whitespace-pre-wrap">{String(val ?? "")}</span>;
          }

          const isLongText = String(val ?? "").length > 40;

          return (
            <div
              key={key}
              className={`flex flex-col p-3 bg-gray-50 rounded-lg border border-gray-100 ${
                isLongText ? "col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4" : ""
              }`}
            >
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                {formatKey(key)}
              </span>
              <div className="mt-1 text-gray-900 text-sm">{displayVal}</div>
            </div>
          );
        })}
      </div>

      {!data.queryInProgress && (
        <div className="sticky bg-white pt-4 pb-4 border-t border-gray-100 flex justify-end z-10 -mx-6 -mb-4 px-6" style={{ bottom: '-16px' }}>
          <button
            onClick={() => onProcess(data.id)}
            className="px-4 py-2 bg-[var(--mainCol)] hover:bg-opacity-90 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
          >
            Process Query
          </button>
        </div>
      )}

      {data.queryInProgress && !data.queryResolve && (
        <div className="sticky bg-white pt-4 pb-4 border-t border-gray-100 flex justify-end z-10 -mx-6 -mb-4 px-6" style={{ bottom: '-16px' }}>
          <button
            onClick={handleResolveClick}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm font-semibold transition-all duration-200"
          >
            Resolve Query
          </button>
        </div>
      )}
    </div>
  );
};

export default Help;
